import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useFboOnboarding } from '../../../hooks/useFboOnboarding';
import StepPersonalBasics from '../components/StepPersonalBasics';
import StepBusinessLookup from '../components/StepBusinessLookup';
import StepMenuStarter from '../components/StepMenuStarter';
import StepSubscriptionCommitment from '../components/StepSubscriptionCommitment';

const INITIAL_ADDRESS = {
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
};

const STAGE_TO_STEP = {
  profile: 1,
  fhrs_lookup: 2,
  menu: 3,
  subscription: 4,
  completed: 4,
};

const TOTAL_STEPS = 4;

const GetStarted = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const {
    fbo,
    loading,
    error: onboardingError,
    saveFbo,
    searchFhrsBusiness,
    saveMenuDraft,
    createCheckoutSession,
    createStripeConnectAccount,
    loadFbo,
  } = useFboOnboarding();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    phoneVerified: false,
    homeAddress: { ...INITIAL_ADDRESS },
    fhrsBusinessId: null,
    fhrsBusinessName: '',
    fhrsBusinessAddress: '',
    fhrsRating: null,
    fhrsRatingImageUrl: '',
    fhrsRatingDate: null,
    registrationStatus: 'not_registered',
    willingToImprove: null,
    needsRegistrationSupport: false,
    menuDraft: null,
    menuStatus: 'not_started',
    stripeAccountId: null,
    kycStatus: 'pending',
    subscriptionStatus: 'none',
  });
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/auth/login?redirect=/seller/get-started');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!fbo) return;

    setFormData((prev) => {
      const address = fbo.home_address || {};
      return {
        ...prev,
        phoneNumber:
          fbo.contact_phone ||
          prev.phoneNumber ||
          user?.phone ||
          user?.user_metadata?.phone ||
          '',
        phoneVerified: !!fbo.phone_verified,
        homeAddress: {
          ...INITIAL_ADDRESS,
          ...prev.homeAddress,
          ...address,
        },
        fhrsBusinessId: fbo.fhrs_business_id || null,
        fhrsBusinessName: fbo.fhrs_business_name || prev.fhrsBusinessName || '',
        fhrsBusinessAddress: fbo.fhrs_business_address || prev.fhrsBusinessAddress || '',
        fhrsRating: fbo.fhrs_rating ?? null,
        fhrsRatingImageUrl: fbo.fhrs_rating_image_url || prev.fhrsRatingImageUrl || '',
        fhrsRatingDate: fbo.fhrs_rating_date || null,
        registrationStatus: fbo.registration_status || prev.registrationStatus,
        willingToImprove:
          typeof fbo.willing_to_improve === 'boolean'
            ? fbo.willing_to_improve
            : prev.willingToImprove,
        needsRegistrationSupport: !!fbo.needs_registration_support,
        menuStatus: fbo.menu_status || prev.menuStatus,
        menuDraft: fbo.menuDraft
          ? {
              source: fbo.menuDraft.source || prev.menuDraft?.source || 'manual',
              items: Array.isArray(fbo.menuDraft.items) ? fbo.menuDraft.items : [],
            }
          : prev.menuDraft,
        stripeAccountId: fbo.stripe_account_id || null,
        kycStatus: fbo.kyc_status || prev.kycStatus || 'pending',
        subscriptionStatus: fbo.subscription_status || prev.subscriptionStatus,
      };
    });

    if (fbo.onboarding_stage) {
      const targetStep = STAGE_TO_STEP[fbo.onboarding_stage];
      if (targetStep) {
        setCurrentStep(targetStep);
      }
    }
  }, [fbo, user]);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const checkoutStatus = search.get('checkout');
    if (checkoutStatus) {
      if (checkoutStatus === 'success') {
        setStatusMessage('Payment successful! Updating your subscription status…');
        loadFbo();
      } else if (checkoutStatus === 'cancelled') {
        setStatusMessage('Checkout cancelled. You can resume whenever you are ready.');
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadFbo]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setErrors({});
  }, [currentStep]);

  const stepMeta = useMemo(
    () => [
      {
        title: 'Personal basics',
        description:
          'Verify your phone number and home address so we can send compliance packs and keep you updated.',
      },
      {
        title: 'Business lookup (FHRS)',
        description:
          'Search the Food Hygiene Rating Scheme to understand your registration status and unlock tailored support.',
      },
      {
        title: 'Menu starter',
        description:
          'Upload an existing menu or draft one with our prompts so customers can see what you offer from day one.',
      },
      {
        title: 'Subscription & commitment',
        description:
          'Complete Stripe Connect payout verification and start the Kinn Starter plan to activate your seller tools.',
      },
    ],
    []
  );

  const currentStepMeta = stepMeta[currentStep - 1] || stepMeta[0];

  const updateFormData = (updates) => {
    setFormData((prev) => {
      const next = { ...prev, ...updates };
      if (updates.homeAddress) {
        next.homeAddress = {
          ...INITIAL_ADDRESS,
          ...prev.homeAddress,
          ...updates.homeAddress,
        };
      }
      return next;
    });
  };

  const validateStep = (step) => {
    const nextErrors = {};

    if (step === 1) {
      if (!formData.phoneNumber) {
        nextErrors.phone = 'Please enter a mobile number we can reach you on.';
      }
      if (!formData.phoneVerified) {
        nextErrors.phone =
          nextErrors.phone ||
          'Verify your phone number so we can enable SMS notifications.';
      }
      const address = formData.homeAddress || {};
      if (!address.line1 || !address.city || !address.postcode) {
        nextErrors.address =
          'Add your street, city, and postcode so we can ship compliance materials.';
      }
    }

    if (step === 2) {
      if (
        formData.registrationStatus === 'registered' &&
        !formData.fhrsBusinessId
      ) {
        nextErrors.registration =
          'Select your business from the search results to confirm the FHRS record.';
      }
      if (
        formData.registrationStatus === 'not_registered' &&
        !formData.needsRegistrationSupport
      ) {
        nextErrors.registration =
          'Confirm that you need registration support before continuing.';
      }
      if (formData.willingToImprove === false) {
        nextErrors.willingToImprove =
          'We can only continue if you are willing to improve your hygiene rating.';
      }
    }

    if (step === 3) {
      const draft = formData.menuDraft;
      if (!draft) {
        nextErrors.menu = 'Create or upload a starter menu before moving on.';
      } else {
        const hasItems = Array.isArray(draft.items) && draft.items.length > 0;
        if (!hasItems && draft.source !== 'upload') {
          nextErrors.menu =
            'Add at least one menu item or upload your existing menu file.';
        }
      }
    }

    if (step === 4) {
      if (formData.willingToImprove === false) {
        nextErrors.commitment =
          'Reaffirm your hygiene improvement commitment to activate selling tools.';
      }
      if (formData.kycStatus !== 'verified') {
        nextErrors.kyc =
          'Complete Stripe Connect onboarding so payouts can be enabled.';
      }
      if (!['trialing', 'active'].includes(formData.subscriptionStatus)) {
        nextErrors.subscription =
          'Finish your Kinn Starter subscription checkout to complete onboarding.';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    const stageMap = {
      1: 'fhrs_lookup',
      2: 'menu',
      3: 'subscription',
      4: ['trialing', 'active'].includes(formData.subscriptionStatus)
        ? 'completed'
        : 'subscription',
    };

    const payload = {
      ...formData,
      onboardingStage: stageMap[currentStep] || 'profile',
    };

    const saveResult = await saveFbo(payload);

    if (!saveResult.success) {
      setErrors((prev) => ({
        ...prev,
        submit: saveResult.error || 'Failed to save your progress. Please try again.',
      }));
      return;
    }

    if (currentStep === 3 && formData.menuDraft) {
      await saveMenuDraft(formData.menuDraft);
    }

    await loadFbo();

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (stageMap[currentStep] === 'completed') {
      navigate('/seller/dashboard');
    } else {
      setStatusMessage(
        'Subscription pending. Complete checkout to finish onboarding.'
      );
    }
  };

  const handleBack = () => {
    if (currentStep === 1) return;
    setCurrentStep((prev) => prev - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepPersonalBasics
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      case 2:
        return (
          <StepBusinessLookup
            formData={formData}
            updateFormData={updateFormData}
            searchFhrsBusiness={searchFhrsBusiness}
            errors={errors}
          />
        );
      case 3:
        return (
          <StepMenuStarter
            formData={formData}
            updateFormData={updateFormData}
            userId={user?.id}
            errors={errors}
          />
        );
      case 4:
        return (
          <StepSubscriptionCommitment
            formData={formData}
            updateFormData={updateFormData}
            createCheckoutSession={createCheckoutSession}
            createStripeConnectAccount={createStripeConnectAccount}
            willingToImprove={formData.willingToImprove}
            errors={errors}
            setStatusMessage={setStatusMessage}
          />
        );
      default:
        return null;
    }
  };

  if (loading && !fbo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FEC51C] mx-auto"></div>
          <p className="mt-4 text-body-md text-gray-600">
            Loading your onboarding progress…
          </p>
        </div>
      </div>
    );
  }

  const showBackButton = currentStep > 1;
  const nextLabel = currentStep === TOTAL_STEPS ? 'Go to dashboard' : 'Continue';

  return (
    <section className="pb-8 pt-[120px] bg-[#F8F8F8] min-h-screen">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8}>
            {/* Fixed progress section outside the scrollable frame */}
            <div className="bg-white rounded-t-[20px] p-6 md:p-8 pb-4 shadow-sm sticky top-[100px] z-10 border-b border-gray-100">
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-body-sm text-text-primary-secondary">Step {currentStep} of {TOTAL_STEPS}</span>
                  <span className="text-body-sm text-text-primary-secondary">{Math.round((currentStep / TOTAL_STEPS) * 100)}% Complete</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Step title and description */}
              <div className="mb-2">
                <h2 className="text-h4 text-text-primary">{currentStepMeta.title}</h2>
                {currentStepMeta.description && (
                  <p className="text-body-lg text-text-primary-secondary mt-2">{currentStepMeta.description}</p>
                )}
              </div>
            </div>

            {/* Scrollable content area */}
            <div className="bg-white rounded-b-[20px] shadow-sm max-h-[calc(100vh-350px)] overflow-y-auto">
              <div className="p-6 md:p-8">
                {onboardingError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-[12px] text-body-sm text-red-700">
                    {onboardingError}
                  </div>
                )}
                {statusMessage && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-[12px] text-body-sm text-blue-800">
                    {statusMessage}
                  </div>
                )}
                {errors.submit && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-[12px] text-body-sm text-red-700">
                    {errors.submit}
                  </div>
                )}
                
                {/* Step content */}
                <div className="mb-6">
                  {renderStep()}
                </div>
              </div>

              {/* Navigation buttons - sticky at bottom */}
              <div className="sticky bottom-0 bg-white border-t border-gray-100 p-6 md:p-8 rounded-b-[20px]">
                <div className="flex gap-4 justify-between">
                  {showBackButton ? (
                    <button
                      onClick={handleBack}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                  ) : <div></div>}

                  <button
                    onClick={handleNext}
                    disabled={loading}
                    className="btn-primary"
                  >
                    {nextLabel}
                  </button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default GetStarted;
